import { useMutation, useQuery, useQueryClient } from 'react-query';

import { CatalogSearchResults } from '~/features/catalog/models/catalog-search-result';
import { useClientRender } from '~/hooks/use-client-render';

import { useUser } from '../../user/hooks/use-user';
import { deleteFavorite } from '../controllers/delete-favorite';
import { getFavorites } from '../controllers/get-favorites';
import { postFavorite } from '../controllers/post-favorite';

export function useFavorites() {
  const { jwt, userId } = useUser();
  const clientReady = useClientRender();
  const queryClient = useQueryClient();

  const {
    data: favorites,
    isLoading: favoritesIsLoading,
    isFetched: favoritesIsFetched,
    isFetching: favoritesIsFetching,
  } = useQuery({
    queryKey: 'favorites',
    queryFn: () => getFavorites({ jwt: jwt!, userId: userId! }),
    enabled: clientReady && Boolean(userId) && Boolean(jwt),
  });

  const addFavorite = useMutation({
    mutationFn: ({ userId, programKey }: { userId: string; programKey: string }) => postFavorite(userId, programKey),
    onMutate: async ({ programKey }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: 'favorites' });

      // Snapshot the previous value
      const previousFavorites = queryClient.getQueryData('favorites');

      // Optimistically update to the new value
      queryClient.setQueryData('favorites', (old: string[] = []) => [...old, programKey]);

      // Return a context object with the snapshotted value
      return { previousFavorites };
    },
    onError: (err, newFavorite, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData('favorites', context?.previousFavorites);
    },
    onSettled: () => {
      // Always refetch after error or success to ensure cache is in sync
      queryClient.invalidateQueries({ queryKey: 'favorites' });
    },
  });

  const removeFavorite = useMutation({
    mutationFn: ({ userId, programKey }: { userId: string; programKey: string }) => deleteFavorite(userId, programKey),
    onMutate: async ({ programKey }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: 'favorites' });

      // Snapshot the previous value
      const previousFavorites = queryClient.getQueryData('favorites');

      // Optimistically update to the new value
      const newFavorites = ((previousFavorites as string[]) || []).filter((key) => key !== programKey);
      queryClient.setQueryData('favorites', newFavorites);
      // Return a context object with both the previous and new values
      return { previousFavorites };
    },
    onError: (_, __, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData('favorites', context?.previousFavorites);
    },
    onSettled: (_, __, context) => {
      // Always refetch after error or success to ensure cache is in sync
      queryClient.invalidateQueries({ queryKey: 'favorites' });
      const cachedFavoritesQueryData =
        queryClient.getQueriesData('catalog')?.filter(([key]) => {
          const queryKey = key as [string, { scope?: string[] }];
          return queryKey[1]?.scope?.includes('favorite');
        }) || [];
      for (const [key, value] of cachedFavoritesQueryData as [string, CatalogSearchResults][]) {
        const prevSearchResultItems = value?.searchResultItems;
        if (prevSearchResultItems) {
          const newSearchResultItems = prevSearchResultItems.filter((item) => item.key !== context?.programKey);
          const nextNumberOfHits = value.numberOfHits - 1;
          queryClient.setQueryData(key, {
            ...value,
            searchResultItems: newSearchResultItems,
            numberOfHits: nextNumberOfHits,
            numberOfPages: value.pageSize > 0 ? Math.ceil(nextNumberOfHits / value.pageSize) : 0,
          });
        }
      }
    },
  });

  return {
    favorites,
    favoritesIsLoading,
    favoritesIsFetched,
    favoritesIsFetching,
    addFavorite,
    removeFavorite,
  };
}
